import type { Counter, Gauge, Histogram, Meter } from "@opentelemetry/api";

import type { DevServerAuthorizationRejectionReason } from "@superblocksteam/library-shared";

import {
  recordINP as coreRecordINP,
  recordLCP as coreRecordLCP,
  recordCLS as coreRecordCLS,
} from "core/telemetry/webVitals";

import { getTelemetry } from "./init";

const MAX_IFRAME_LOAD_TIME_MS = 120_000;
type DevServerHeartbeatOutcome =
  | "success"
  | "failure"
  | "timeout"
  | "timeout_grace"
  | "timeout_receiving";
type DevServerHeartbeatBaseAttributes = {
  // The app tree passes its `ConnectionStatus` union (store/slices/codeMode/types)
  // or "unknown"; typed as string here because core/ can't import app-store types.
  connection_status: string;
  is_user_active: "true" | "false";
  // Consecutive lapsed pings at close time; bounded label present only on
  // "timeout" outcomes.
  lapses?: string;
};
type DevServerHeartbeatDurationAttributes = DevServerHeartbeatBaseAttributes & {
  outcome: DevServerHeartbeatOutcome;
};
type ExecutionScopedTokenRemintScope =
  | "apps:preview"
  | "apps:update"
  | "apps:view";
type ExecutionScopedTokenRemintAttributes =
  | {
      "token.remint.outcome": "fallback";
      "token.remint.reason":
        | "context_changed"
        | "execution_context_unavailable"
        | "mint_failed";
      "token.remint.scope": ExecutionScopedTokenRemintScope;
    }
  | {
      "token.remint.outcome": "success";
      "token.remint.scope": ExecutionScopedTokenRemintScope;
    };
type DevServerAuthorizationEvent =
  | "authorization_republish_sent"
  | "authorization_republish_skipped_no_iframe"
  | "authorization_republish_skipped_no_token"
  | "authorization_rejected"
  | "mint_retry_exhausted"
  | "refresh_retry_exhausted";

export type OAuthPopupAttemptEvent =
  | "channel_unavailable"
  | "grace_expired"
  | "popup_blocked"
  | "popup_closed"
  | "settled_channel"
  | "settled_opener"
  | "severed_wait"
  | "superseded";

class UIMetrics {
  private static _meter: Meter | undefined;

  private static get meter(): Meter {
    if (!UIMetrics._meter) {
      UIMetrics._meter = getTelemetry().getMeter("superblocks-ui");
    }
    return UIMetrics._meter;
  }

  private static _reconnectLatencyGauge: Gauge | undefined;
  private static get reconnectLatencyGauge(): Gauge {
    if (!UIMetrics._reconnectLatencyGauge) {
      UIMetrics._reconnectLatencyGauge = UIMetrics.meter.createGauge(
        "ui_reconnect_latency_ms_gauge",
        {
          description: "Browser latency for reconnecting to cloud",
          unit: "ms",
        },
      );
    }
    return UIMetrics._reconnectLatencyGauge;
  }

  private static _editorLoadTimeGauge: Gauge | undefined;
  private static get editorLoadTimeGauge(): Gauge {
    if (!UIMetrics._editorLoadTimeGauge) {
      UIMetrics._editorLoadTimeGauge = UIMetrics.meter.createGauge(
        "ui_editor_load_time_ms_gauge",
        {
          description: "Time to load editor",
          unit: "ms",
        },
      );
    }
    return UIMetrics._editorLoadTimeGauge;
  }

  private static _iframeLoadTimeHistogram: Histogram | undefined;
  private static get iframeLoadTimeHistogram(): Histogram {
    if (!UIMetrics._iframeLoadTimeHistogram) {
      UIMetrics._iframeLoadTimeHistogram = UIMetrics.meter.createHistogram(
        "ui_iframe_load_time_ms",
        {
          description: "Time to load iframe",
          unit: "ms",
        },
      );
    }
    return UIMetrics._iframeLoadTimeHistogram;
  }

  private static _executionScopedTokenRemints: Counter | undefined;
  private static get executionScopedTokenRemints(): Counter {
    if (!UIMetrics._executionScopedTokenRemints) {
      UIMetrics._executionScopedTokenRemints = UIMetrics.meter.createCounter(
        "ui_execution_scoped_token_remints_total",
        {
          description:
            "Count of scoped execution token remints after session authorization changes",
        },
      );
    }
    return UIMetrics._executionScopedTokenRemints;
  }

  private static _devServerConnectionDuration: Histogram | undefined;
  private static get devServerConnectionDuration(): Histogram {
    if (!UIMetrics._devServerConnectionDuration) {
      UIMetrics._devServerConnectionDuration = UIMetrics.meter.createHistogram(
        "dev_server_connection_duration_ms",
        {
          description:
            "Duration of dev server connection phases, partitioned by the `phase` attribute",
          unit: "ms",
          advice: {
            explicitBucketBoundaries: [
              100, 250, 500, 1000, 2500, 5000, 7500, 10000, 15000, 20000, 30000,
              45000, 60000, 90000, 120000,
            ],
          },
        },
      );
    }
    return UIMetrics._devServerConnectionDuration;
  }

  private static _devServerConnectedDuration: Histogram | undefined;
  private static get devServerConnectedDuration(): Histogram {
    if (!UIMetrics._devServerConnectedDuration) {
      UIMetrics._devServerConnectedDuration = UIMetrics.meter.createHistogram(
        "dev_server_connected_duration_ms",
        {
          description:
            "Duration that the websocket connection to the dev server was active",
          unit: "ms",
        },
      );
    }
    return UIMetrics._devServerConnectedDuration;
  }

  private static _devServerConnectionAttempts: Counter | undefined;
  private static get devServerConnectionAttempts(): Counter {
    if (!UIMetrics._devServerConnectionAttempts) {
      UIMetrics._devServerConnectionAttempts = UIMetrics.meter.createCounter(
        "dev_server_connection_attempts_total",
        {
          description:
            "Count of dev-server connection attempts and outcomes by phase (overall, server_connect, health, readiness, editor_websocket, app_websocket, iframe_ready)",
        },
      );
    }
    return UIMetrics._devServerConnectionAttempts;
  }

  private static _liveEditSessionReads: Counter | undefined;
  private static get liveEditSessionReads(): Counter {
    if (!UIMetrics._liveEditSessionReads) {
      UIMetrics._liveEditSessionReads = UIMetrics.meter.createCounter(
        "ui_live_edit_session_reads_total",
        {
          description:
            "Count of SABS live-edit session re-reads made while a health check fails, by `sabs.live_edit.read_outcome`: a session status (starting/active/terminated/terminating), 'unrecognized' when SABS stated no status for the session, or 'unreadable' when the read itself failed",
        },
      );
    }
    return UIMetrics._liveEditSessionReads;
  }

  static recordLiveEditSessionRead(outcome: string) {
    UIMetrics.liveEditSessionReads.add(1, {
      "sabs.live_edit.read_outcome": outcome,
    });
  }

  private static _appRenderErrors: Counter | undefined;
  private static get appRenderErrors(): Counter {
    if (!UIMetrics._appRenderErrors) {
      UIMetrics._appRenderErrors = UIMetrics.meter.createCounter(
        "ui_app_render_error_total",
        {
          description:
            "Count of app failures that surfaced the pre-render 'Fix with Clark' error overlay, by `kind` (build/module_load/runtime/unknown) and `source` (early_relay/in_app/build_preload/build_overlay)",
        },
      );
    }
    return UIMetrics._appRenderErrors;
  }

  static recordAppRenderError(attributes?: Record<string, string | number>) {
    UIMetrics.appRenderErrors.add(1, { ...attributes });
  }

  private static _oauthPopupAttemptEvents: Counter | undefined;
  private static get oauthPopupAttemptEvents(): Counter {
    if (!UIMetrics._oauthPopupAttemptEvents) {
      UIMetrics._oauthPopupAttemptEvents = UIMetrics.meter.createCounter(
        "ui_oauth_popup_attempt_events_total",
        {
          description:
            "Count of OAuth connect popup lifecycle events by `event` (popup_blocked/severed_wait/settled_opener/settled_channel/popup_closed/grace_expired/superseded/channel_unavailable) and `result` (success/error/cancelled/silent)",
        },
      );
    }
    return UIMetrics._oauthPopupAttemptEvents;
  }

  static recordOAuthPopupAttemptEvent(
    event: OAuthPopupAttemptEvent,
    attributes?: Record<string, string>,
  ) {
    UIMetrics.oauthPopupAttemptEvents.add(1, { event, ...attributes });
  }

  private static _cloneDatabaseSetupTurns: Counter | undefined;
  private static get cloneDatabaseSetupTurns(): Counter {
    if (!UIMetrics._cloneDatabaseSetupTurns) {
      UIMetrics._cloneDatabaseSetupTurns = UIMetrics.meter.createCounter(
        "ui_clone_database_setup_total",
        {
          description:
            "Count of automatic app-database setup turns for cloned apps, by `clone.database_setup.stage`: 'queued' when the clone handed the prompt to the new app, 'started' when the editor got the turn generating, 'failed' when the editor had the prompt but could not start the turn. Queued minus started minus failed is how often the browser handoff was lost.",
        },
      );
    }
    return UIMetrics._cloneDatabaseSetupTurns;
  }

  static recordCloneDatabaseSetup(stage: "failed" | "queued" | "started") {
    UIMetrics.cloneDatabaseSetupTurns.add(1, {
      "clone.database_setup.stage": stage,
    });
  }

  private static _devServerAuthorizationEvents: Counter | undefined;
  private static get devServerAuthorizationEvents(): Counter {
    if (!UIMetrics._devServerAuthorizationEvents) {
      UIMetrics._devServerAuthorizationEvents = UIMetrics.meter.createCounter(
        "dev_server_authorization_events_total",
        {
          description:
            "Count of dev-server authorization rejection, republish, and retry-exhaustion events",
        },
      );
    }
    return UIMetrics._devServerAuthorizationEvents;
  }

  static recordDevServerAuthorizationEvent(
    event: DevServerAuthorizationEvent,
    attributes?: {
      reason:
        | DevServerAuthorizationRejectionReason
        | "missing_execution_context";
    },
  ) {
    UIMetrics.devServerAuthorizationEvents.add(1, { event, ...attributes });
  }

  private static _highlightingFailures: Counter | undefined;
  private static get highlightingFailures(): Counter {
    if (!UIMetrics._highlightingFailures) {
      UIMetrics._highlightingFailures = UIMetrics.meter.createCounter(
        "ui_highlighting_failure_total",
        {
          description:
            "Count of failed syntax-highlighter loads, code-block tokenizations, or lazy markdown plugin imports. Labels stay bounded: highlighting.kind (highlighter_load|highlight|plugin_load) and optional markdown.plugin (math|mermaid).",
        },
      );
    }
    return UIMetrics._highlightingFailures;
  }

  /**
   * Record a user-visible highlighting / markdown-plugin degradation.
   * Tags stay bounded: `highlighting.kind` and optional `markdown.plugin`.
   */
  static recordHighlightingFailure(attributes: {
    "highlighting.kind": "highlighter_load" | "highlight" | "plugin_load";
    "markdown.plugin"?: "math" | "mermaid";
  }) {
    UIMetrics.highlightingFailures.add(1, { ...attributes });
  }

  private static _devServerConnectionExhausted: Counter | undefined;
  private static get devServerConnectionExhausted(): Counter {
    if (!UIMetrics._devServerConnectionExhausted) {
      UIMetrics._devServerConnectionExhausted = UIMetrics.meter.createCounter(
        "dev_server_connection_exhausted_total",
        {
          description:
            "Count of editor<->dev-box connection sequences abandoned after the retry budget was exhausted - each increment is one user shown a refresh-required state. Deliberately separate from dev_server_connection_attempts_total (per-attempt units).",
        },
      );
    }
    return UIMetrics._devServerConnectionExhausted;
  }

  /**
   * Record a terminal connection give-up. One increment = one user forced
   * to refresh. Tags stay bounded: `connection.type` and `connection.target`
   * (dotted OTel names — this is newly added telemetry per the org standard).
   */
  static recordConnectionExhausted(
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionExhausted.add(1, { ...attributes });
  }

  private static _chatDurableReads: Counter | undefined;
  private static get chatDurableReads(): Counter {
    if (!UIMetrics._chatDurableReads) {
      UIMetrics._chatDurableReads = UIMetrics.meter.createCounter(
        "ui_chat_durable_read_total",
        {
          description:
            "Count of pod-independent Clark chat history reads by `chat.read_path` (recent/older) and `chat.outcome` (success/error). The editor's only durable read path, so its error rate is what separates 'no history' from 'history we could not fetch'.",
        },
      );
    }
    return UIMetrics._chatDurableReads;
  }

  static recordChatDurableRead(attributes: {
    "chat.read_path": "recent" | "older";
    "chat.outcome": "success" | "error";
  }) {
    UIMetrics.chatDurableReads.add(1, { ...attributes });
  }

  private static _chatScrollbackExhausted: Counter | undefined;
  private static get chatScrollbackExhausted(): Counter {
    if (!UIMetrics._chatScrollbackExhausted) {
      UIMetrics._chatScrollbackExhausted = UIMetrics.meter.createCounter(
        "ui_chat_scrollback_exhausted_total",
        {
          description:
            "Count of Clark chat sessions that stopped offering older history after consecutive durable-read failures - each increment is one user whose transcript is truncated at an arbitrary point. Deliberately separate from ui_chat_durable_read_total (per-attempt units).",
        },
      );
    }
    return UIMetrics._chatScrollbackExhausted;
  }

  static recordChatScrollbackExhausted(attributes?: Record<string, string>) {
    UIMetrics.chatScrollbackExhausted.add(1, { ...attributes });
  }

  private static _devServerHeartbeatDuration: Histogram | undefined;
  private static get devServerHeartbeatDuration(): Histogram {
    if (!UIMetrics._devServerHeartbeatDuration) {
      UIMetrics._devServerHeartbeatDuration = UIMetrics.meter.createHistogram(
        "dev_server_heartbeat_duration_ms",
        {
          description: "Browser websocket heartbeat ping duration",
          unit: "ms",
          advice: {
            explicitBucketBoundaries: [
              10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 15000, 30000,
            ],
          },
        },
      );
    }
    return UIMetrics._devServerHeartbeatDuration;
  }

  private static _devServerHeartbeatAttempts: Counter | undefined;
  private static get devServerHeartbeatAttempts(): Counter {
    if (!UIMetrics._devServerHeartbeatAttempts) {
      UIMetrics._devServerHeartbeatAttempts = UIMetrics.meter.createCounter(
        "dev_server_heartbeat_attempts_total",
        {
          description:
            "Count of browser websocket heartbeat ping attempts and outcomes",
        },
      );
    }
    return UIMetrics._devServerHeartbeatAttempts;
  }

  static recordReconnectLatency(
    latency: number,
    attributes?: Record<string, string>,
  ) {
    UIMetrics.reconnectLatencyGauge.record(latency, attributes);
  }

  static recordEditorLoadTime(
    loadTime: number,
    attributes?: Record<string, string>,
  ) {
    UIMetrics.editorLoadTimeGauge.record(loadTime, attributes);
  }

  static recordExecutionScopedTokenRemint(
    attributes: ExecutionScopedTokenRemintAttributes,
  ) {
    UIMetrics.executionScopedTokenRemints.add(1, attributes);
  }

  static recordIframeLoadTime(
    loadTime: number,
    attributes?: Record<string, string>,
  ) {
    if (loadTime < 0 || loadTime > MAX_IFRAME_LOAD_TIME_MS) {
      console.warn(
        `[UIMetrics] Discarding iframe load time: ${loadTime}ms (valid range: 0-${MAX_IFRAME_LOAD_TIME_MS}ms)`,
      );
      return;
    }
    UIMetrics.iframeLoadTimeHistogram.record(loadTime, attributes);
  }

  static recordHealthCheckDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "health",
      ...attributes,
    });
  }

  static recordReadinessDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "readiness",
      ...attributes,
    });
  }

  static recordEditorWebsocketDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "editor_websocket",
      ...attributes,
    });
  }

  static recordAppWebsocketDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "app_websocket",
      ...attributes,
    });
  }

  static recordOverallConnectionDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "overall",
      ...attributes,
    });
  }

  /**
   * Record the user-perceived latency from connection start to the iframe
   * reporting `sb-ready`.
   */
  static recordIframeReadyDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionDuration.record(duration, {
      phase: "iframe_ready",
      ...attributes,
    });
  }

  /**
   * Record a connection attempt and its outcome for a given phase.
   *
   * Phases form a coarse pipeline: `server_connect` (HTTP /connect),
   * `health` (`_sb_health`), `readiness` (`_sb_connect`), `editor_websocket`,
   * `app_websocket`, and `iframe_ready` (`sb-ready`). Outcomes capture both
   * lifecycle (`start`) and terminal states (`success`, `failure`, `timeout`,
   * and `session_ended` when SABS reports the live-edit session stopped).
   */
  static recordConnectionAttempt(
    phase:
      | "server_connect"
      | "health"
      | "readiness"
      | "editor_websocket"
      | "app_websocket"
      | "iframe_ready"
      | "overall",
    outcome: "start" | "success" | "failure" | "timeout" | "session_ended",
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectionAttempts.add(1, {
      phase,
      outcome,
      ...attributes,
    });
  }

  static recordDevServerHeartbeatAttempt(
    outcome: DevServerHeartbeatOutcome,
    attributes?: DevServerHeartbeatBaseAttributes,
  ) {
    UIMetrics.devServerHeartbeatAttempts.add(1, {
      outcome,
      ...attributes,
    });
  }

  static recordDevServerHeartbeatDuration(
    duration: number,
    attributes?: DevServerHeartbeatDurationAttributes,
  ) {
    UIMetrics.devServerHeartbeatDuration.record(duration, attributes);
  }

  static recordWebsocketConnectedDuration(
    duration: number,
    attributes?: Record<string, string | number>,
  ) {
    UIMetrics.devServerConnectedDuration.record(duration, attributes);
  }

  static recordINP(value: number, attributes?: Record<string, string>) {
    coreRecordINP(value, attributes);
  }

  static recordLCP(value: number, attributes?: Record<string, string>) {
    coreRecordLCP(value, attributes);
  }

  static recordCLS(value: number, attributes?: Record<string, string>) {
    coreRecordCLS(value, attributes);
  }
}

export default UIMetrics;
